import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import './main.css';
import SkipLink from './components/SkipLink';
import WizardHost from './components/WizardHost';
import Portal from './components/portal/Portal';
import { useResponsive } from './hooks/useResponsive';
import { LoggedOutPage } from './components/LoggedOutPage';
import { LogoutWarningModal } from './components/LogoutWarningModal';
import { useSessionManager } from './hooks/useSessionManager';
import type { FlowId } from './types';
import { resolveFlow, normalizeFlowId } from './wizard/flowRegistry';

const WizardRoute = () => {
  const { flowId } = useParams<{ flowId?: string }>();
  const normalizedFlowId: FlowId = normalizeFlowId(flowId ?? undefined);
  const flow = resolveFlow(normalizedFlowId);
  if (flowId && flowId !== normalizedFlowId) {
    return <Navigate to={`/${normalizedFlowId}`} replace />;
  }
  return <WizardHost key={flow.id} />;
};

function App() {
  const [fontSizeMultiplier, setFontSizeMultiplier] = useState(1.0);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const { isMobile } = useResponsive();

  const { showWarning, countdown, dismissWarning, recordActivity } = useSessionManager({
    inactivityTimeout: 5 * 60 * 1000,
    warningDuration: 30 * 1000,
    onLogout: () => setIsLoggedOut(true)
  });

  useEffect(() => {
    document.documentElement.style.setProperty('--font-size-multiplier', fontSizeMultiplier.toString());
  }, [fontSizeMultiplier]);

  const handleFontIncrease = () => {
    if (fontSizeMultiplier < 1.4) {
      setFontSizeMultiplier(value => Math.round((value + 0.1) * 10) / 10);
    }
  };

  const handleFontDecrease = () => {
    if (fontSizeMultiplier > 0.8) {
      setFontSizeMultiplier(value => Math.round((value - 0.1) * 10) / 10);
    }
  };

  const handleFontReset = () => {
    setFontSizeMultiplier(1.0);
  };

  if (isLoggedOut) {
    return <LoggedOutPage />;
  }

  return (
    <>
      <SkipLink />
      <main id="app-container" onClick={recordActivity} data-device-context={isMobile ? 'mobile' : 'desktop'}>
        <Routes>
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/:profileId" element={<Portal />} />
          <Route path="/:flowId" element={<WizardRoute />} />
          <Route path="/:flowId/wizard/:stepId" element={<WizardRoute />} />
          <Route path="/wizard/:stepId" element={<Navigate to="/pas/wizard/:stepId" replace />} />
          <Route path="/" element={<Navigate to="/pas" replace />} />
          <Route path="*" element={<Navigate to="/pas" replace />} />
        </Routes>
      </main>

      <footer className="app-footer" aria-label="Sidefod">
        <div className="footer-links" aria-label="Juridisk information">
          <a href="#" aria-label="Privatlivspolitik">Privatlivspolitik</a>
          <a href="#" aria-label="Tilgængelighedserklæring">Tilgængelighed</a>
          <a href="#" aria-label="Cookies">Cookies</a>
        </div>
        <div className="accessibility-controls" aria-label="Tekststørrelse">
          <span className="font-size-label">Tekststørrelse:</span>
          <button id="font-decrease" className="size-btn size-btn--decrease" aria-label="Formindsk tekststørrelse (A-)" onClick={handleFontDecrease}>
            A-
          </button>
          <button id="font-reset" className="size-btn size-btn--reset" aria-label="Nulstil tekststørrelse (A)" onClick={handleFontReset}>
            A
          </button>
          <button id="font-increase" className="size-btn size-btn--increase" aria-label="Forstør tekststørrelse (A+)" onClick={handleFontIncrease}>
            A+
          </button>
          <span className="size-readout" aria-live="polite">{Math.round(fontSizeMultiplier * 100)}%</span>
        </div>
      </footer>

      <LogoutWarningModal
        isOpen={showWarning}
        countdown={countdown}
        onDismiss={dismissWarning}
      />
    </>
  );
}

export default App;
